/**
 * Shifts React Query hooks.
 *
 * Day 14 Sprint 1D #3 + #4.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { shiftsService } from "@/services/shiftsService";
import type {
  OpenShiftInput,
  CloseShiftInput,
  ShiftsListFilters,
} from "@/types/shifts.types";

const NS = ["shifts"] as const;
export const currentKey = (businessId?: string) =>
  [...NS, "current", businessId || "default"] as const;
export const listKey = (filters: ShiftsListFilters = {}) =>
  [...NS, "list", filters] as const;
export const detailKey = (id: string) => [...NS, "detail", id] as const;
export const zReportKey = (id: string) => [...NS, "z-report", id] as const;

export function useCurrentShift(businessId?: string, enabled = true) {
  return useQuery({
    queryKey: currentKey(businessId),
    queryFn: () => shiftsService.getCurrent(businessId),
    staleTime: 30 * 1000, // refetch fairly often; cashier's view should feel live
    enabled,
  });
}

export function useShifts(filters: ShiftsListFilters = {}) {
  return useQuery({
    queryKey: listKey(filters),
    queryFn: () => shiftsService.list(filters),
    staleTime: 60 * 1000,
  });
}

export function useShift(id: string | null) {
  return useQuery({
    queryKey: detailKey(id || ""),
    queryFn: () => shiftsService.get(id!),
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });
}

export function useZReport(id: string | null) {
  return useQuery({
    queryKey: zReportKey(id || ""),
    queryFn: () => shiftsService.getZReport(id!),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000, // closed shifts don't change
  });
}

export function useOpenShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OpenShiftInput) => shiftsService.open(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NS });
    },
  });
}

export function useCloseShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CloseShiftInput }) =>
      shiftsService.close(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NS });
    },
  });
}

export function useForceCloseShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CloseShiftInput }) =>
      shiftsService.forceClose(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NS });
    },
  });
}
